import React, { useContext, useEffect, useState } from "react";
import { ESDataTable } from "../../controls/ESDataTable";
import {
  ConfigurationDetailInsert,
  getAllConfigurationDetail,
  getOrganizationThemeColors,
  updateOrganizationThemeColors,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  Row,
} from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { UserContext } from "../../routes/routes";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import {
  checkIfSuperAdmin,
  formatDateCommon,
  getCachedData,
  Option,
} from "../../utils/common";
import { applyOrganizationTheme } from "../../utils/themeHelper";
import Footer from "../footer/Footer";
import Header from "../header/Header";
import Loader from "../loader/Loader";
import SubHeader from "../subheader/Subheader";
import "./Settings.scss";

interface ConfigurationDetail {
  ConfigurationDetailId: number;
  ConfigurationId: number;
  OrganizationId: number;
  Value: string;
  IsActive: boolean;
  CreateDate: string;
  UpdateDate: string;
  CreateUserId: number | null;
  UpdateUserId: number | null;
  InternalKey: string | null;
  ConcurrencyKey: string | null;
  ConfigurationDataType: string;
  ConfigurationName: string;
}

const Settings: React.FC<{}> = () => {
  const [selectedRows, setSelectedRows] = useState<ConfigurationDetail[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [defaultOraganizationDropDown, setDefaultOraganizationDropDown] =
    useState<any>(null);
  const [organizationId, setOrganizationId] = useState<number | 0>(0);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { appContextdata } = useContext<any>(UserContext);
  const [loading, setLoading] = useState(false);
  const methods = useForm();

  const [configurationsDetail, setConfigurationsDetail] = useState<
    ConfigurationDetail[]
  >([]);

  // Theme Colors state
  const [primaryColor, setPrimaryColor] = useState<string>(
    localStorage.getItem("org_primary_color") || "#2ca9b8"
  );
  const [secondaryColor, setSecondaryColor] = useState<string>(
    localStorage.getItem("org_secondary_color") || "#939597"
  );
  const [editingType, setEditingType] = useState<"primary" | "secondary">("primary");
  const [tempColor, setTempColor] = useState<string>(
    localStorage.getItem("org_primary_color") || "#2ca9b8"
  );
  const [savingColor, setSavingColor] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);

  const fetchConfigurationDetails = async () => {
    try {
      setLoading(true);
      const response: any = await axiosPost(getAllConfigurationDetail, {
        OrganizationId: organizationId,
      });
      if (response?.data?.ExtraData) {
        setConfigurationsDetail(response?.data?.ExtraData);

        const activeRows = response?.data?.ExtraData?.filter(
          (item: ConfigurationDetail) => item?.IsActive
        );

        setSelectedRows(activeRows);
      }
    } catch (error) {
      showToast("Failed to load configuration details.", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Fetch current theme colors for organization
  const fetchThemeColors = async () => {
    try {
      const orgId = organizationId === 0 ? (appContextdata?.Organization || appContextdata?.OrganizationId) : organizationId;
      if (!orgId) {
        const cachedP = localStorage.getItem("org_primary_color") || "#2ca9b8";
        const cachedS = localStorage.getItem("org_secondary_color") || "#939597";
        setPrimaryColor(cachedP);
        setSecondaryColor(cachedS);
        return;
      }

      const response: any = await axiosPost(getOrganizationThemeColors, {
        OrganizationId: orgId,
      });
      if (response?.data?.Status === 200 && response?.data?.ExtraData) {
        const pColor = response.data.ExtraData.PrimaryColor || "#2ca9b8";
        const sColor = response.data.ExtraData.SecondaryColor || "#939597";
        setPrimaryColor(pColor);
        setSecondaryColor(sColor);
        applyOrganizationTheme(pColor, sColor);
      }
    } catch {
      const cachedP = localStorage.getItem("org_primary_color") || "#2ca9b8";
      const cachedS = localStorage.getItem("org_secondary_color") || "#939597";
      setPrimaryColor(cachedP);
      setSecondaryColor(cachedS);
      applyOrganizationTheme(cachedP, cachedS);
    }
  };

  useEffect(() => {
    fetchConfigurationDetails();
    fetchThemeColors();
  }, [organizationId, appContextdata?.Organization]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);

      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData("organizations")) as Option[];
        setOrganizations(options);
        const defaultOption: any = options?.find(
          (option) => option?.value === appContextdata?.Organization
        );
        handleOrganizationChange(defaultOption);
        setDefaultOraganizationDropDown(defaultOption);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const handleRowCheckboxToggle = async (row: ConfigurationDetail) => {
    const isSelected = selectedRows.some(
      (selectedRow) => selectedRow?.ConfigurationId === row?.ConfigurationId
    );

    if (isSelected) {
      setSelectedRows(
        selectedRows.filter(
          (selectedRow) => selectedRow?.ConfigurationId !== row?.ConfigurationId
        )
      );

      try {
        setLoading(true);
        const response: any = await axiosPost(ConfigurationDetailInsert, {
          ConfigurationId: row?.ConfigurationId,
          Value: row.Value,
          IsActive: false,
          CreateDate: new Date(),
          UpdateDate: new Date(),
        });
        if (response?.status === 200) {
          const message = "Configuration DeActivated Successfully";
          showToast(message, ToastTypeCode?.SUCCESS);
        }
      } catch (error) {
        setError("Error fetching data");
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedRows([...selectedRows, row]);

      try {
        setLoading(true);
        const response: any = await axiosPost(ConfigurationDetailInsert, {
          ConfigurationId: row?.ConfigurationId,
          Value: row?.Value,
          IsActive: true,
        });
        if (response?.status === 200) {
          const message = "Configuration activated successfully.";
          showToast(message, ToastTypeCode?.SUCCESS);
        }
      } catch (error) {
        setError("Failed to activate configuration.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOrganizationChange = (selectedOption: Option | null) => {
    setDefaultOraganizationDropDown(null);
    setOrganizationId(selectedOption?.value ?? 0);
  };

  const handleSaveThemeColor = async () => {
    if (!tempColor || !/^#[0-9A-Fa-f]{3,8}$/.test(tempColor)) {
      showToast("Please enter a valid hex color (e.g. #2ca9b8)", ToastTypeCode?.ERROR);
      return;
    }

    setSavingColor(true);
    try {
      const orgId = organizationId === 0 ? (appContextdata?.Organization || appContextdata?.OrganizationId) : organizationId;
      const newPrimary = editingType === "primary" ? tempColor : primaryColor;
      const newSecondary = editingType === "secondary" ? tempColor : secondaryColor;

      const response: any = await axiosPost(updateOrganizationThemeColors, {
        OrganizationId: orgId,
        PrimaryColor: newPrimary,
        SecondaryColor: newSecondary,
      });

      if (response?.data?.Status === 200) {
        if (editingType === "primary") {
          setPrimaryColor(tempColor);
        } else {
          setSecondaryColor(tempColor);
        }
        applyOrganizationTheme(newPrimary, newSecondary);
        showToast(`${editingType === "primary" ? "Primary" : "Secondary"} color updated successfully.`, ToastTypeCode?.SUCCESS);
        setShowColorModal(false);
        await fetchThemeColors();
      } else if (response?.data?.ErrorMessage) {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch {
      showToast("Error saving theme color.", ToastTypeCode?.ERROR);
    } finally {
      setSavingColor(false);
    }
  };

  // Combine real configuration details with Primary_Color and Secondary_Color entries
  const tableData = [
    ...configurationsDetail,
    {
      ConfigurationDetailId: -99,
      ConfigurationId: -99,
      OrganizationId: organizationId,
      ConfigurationName: "Primary_Color",
      ConfigurationDataType: `Brand Color (${primaryColor})`,
      Value: primaryColor,
      IsActive: true,
      CreateDate: new Date().toISOString(),
      UpdateDate: new Date().toISOString(),
      CreateUserId: null,
      UpdateUserId: null,
      InternalKey: null,
      ConcurrencyKey: null,
    } as ConfigurationDetail,
    {
      ConfigurationDetailId: -98,
      ConfigurationId: -98,
      OrganizationId: organizationId,
      ConfigurationName: "Secondary_Color",
      ConfigurationDataType: `Secondary Color (${secondaryColor})`,
      Value: secondaryColor,
      IsActive: true,
      CreateDate: new Date().toISOString(),
      UpdateDate: new Date().toISOString(),
      CreateUserId: null,
      UpdateUserId: null,
      InternalKey: null,
      ConcurrencyKey: null,
    } as ConfigurationDetail,
  ];

  const columns = [
    {
      name: "Select",
      selector: (row: ConfigurationDetail) => {
        if (row.ConfigurationName === "Primary_Color") {
          return (
            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
                backgroundColor: primaryColor,
                border: "1px solid #ced4da",
              }}
              title={`Primary: ${primaryColor}`}
            />
          );
        }
        if (row.ConfigurationName === "Secondary_Color") {
          return (
            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
                backgroundColor: secondaryColor,
                border: "1px solid #ced4da",
              }}
              title={`Secondary: ${secondaryColor}`}
            />
          );
        }
        return (
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedRows.some(
              (selectedRow) => selectedRow.ConfigurationId === row.ConfigurationId
            )}
            onChange={() => handleRowCheckboxToggle(row)}
            data-tour="enable-captcha"
          />
        );
      },
      grow: 1,
      width: "70px",
    },
    {
      name: "Configuration Name",
      selector: (row: ConfigurationDetail) => row.ConfigurationName,
      sortable: true,
      sortField: "ConfigurationName",
      grow: 2,
    },
    {
      name: "ConfigurationDataType",
      selector: (row: ConfigurationDetail) => {
        if (row.ConfigurationName === "Primary_Color") {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Primary Color ({primaryColor})</span>
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "3px",
                  backgroundColor: primaryColor,
                  border: "1px solid #ced4da",
                  display: "inline-block",
                }}
              />
            </div>
          );
        }
        if (row.ConfigurationName === "Secondary_Color") {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Secondary Color ({secondaryColor})</span>
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "3px",
                  backgroundColor: secondaryColor,
                  border: "1px solid #ced4da",
                  display: "inline-block",
                }}
              />
            </div>
          );
        }
        return row.ConfigurationDataType;
      },
      sortable: true,
      sortField: "ConfigurationDataType",
      grow: 3,
    },
    {
      name: "Last Modified",
      selector: (row: ConfigurationDetail) => formatDateCommon(row.CreateDate),
      sortable: true,
      sortField: "CreateDate",
      grow: 2,
    },
    {
      name: "Action",
      selector: (row: ConfigurationDetail) => {
        if (row.ConfigurationName === "Primary_Color") {
          return (
            <Button
              variant="outline-primary"
              size="sm"
              style={{
                padding: "3px 14px",
                fontSize: "13px",
                borderRadius: "5px",
                fontWeight: 500,
              }}
              onClick={() => {
                setEditingType("primary");
                setTempColor(primaryColor);
                setShowColorModal(true);
              }}
            >
              Edit Color
            </Button>
          );
        }
        if (row.ConfigurationName === "Secondary_Color") {
          return (
            <Button
              variant="outline-secondary"
              size="sm"
              style={{
                padding: "3px 14px",
                fontSize: "13px",
                borderRadius: "5px",
                fontWeight: 500,
              }}
              onClick={() => {
                setEditingType("secondary");
                setTempColor(secondaryColor);
                setShowColorModal(true);
              }}
            >
              Edit Color
            </Button>
          );
        }
        return "-";
      },
      grow: 1,
      width: "130px",
    },
  ];

  return (
    <>
      <div className="wrap-userlocation wrap-settings">
        <Header />
        <SubHeader />
        <div className="usertemplate-blk">
          <Container fluid>
            <Card>
              <CardHeader>
                <Row>
                  <Col lg={6}>
                    <h2 className="page-title">Settings</h2>
                  </Col>
                  <Col lg={6}>
                    <div className="search-usertemplate settings-organization-filter">
                      {isSuperAdmin && (
                        <FormProvider {...methods}>
                          {organizations && organizations?.length >= 1 && (
                            <ESSearchDropdown
                              name="OrganizationId"
                              placeholder="Select Organization"
                              options={organizations}
                              onChange={handleOrganizationChange}
                              defaultValue={defaultOraganizationDropDown}
                            />
                          )}
                        </FormProvider>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {error && <p>{error}</p>}
                <div className="table-responsive">
                  <ESDataTable
                    columns={columns}
                    data={tableData}
                    paginationTotalRows={tableData.length}
                    onChangePage={() => {}}
                    onSort={() => {}}
                    onChangeRowsPerPage={() => {}}
                  />
                </div>
              </CardBody>
            </Card>
          </Container>
        </div>

        {/* Edit Theme Color Modal */}
        <Modal
          show={showColorModal}
          onHide={() => setShowColorModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
              Edit {editingType === "primary" ? "Primary" : "Secondary"} Theme Color
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: "18px" }}>
              {editingType === "primary"
                ? "Configure the Primary brand color for buttons, active navigation tabs, highlights, and icons for this organization."
                : "Configure the Secondary color for cancel/back buttons and subtle accents for this organization."}
            </p>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontWeight: 500, marginBottom: "8px", display: "block", fontSize: "14px" }}>
                Select Color
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="color"
                  value={tempColor}
                  onChange={(e) => setTempColor(e.target.value)}
                  style={{
                    width: "44px",
                    height: "38px",
                    padding: "2px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  value={tempColor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val) || val === "" || val === "#") {
                      setTempColor(val);
                    }
                  }}
                  placeholder="#2ca9b8"
                  maxLength={7}
                  style={{ maxWidth: "130px" }}
                />
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "4px",
                    backgroundColor: tempColor || (editingType === "primary" ? "#2ca9b8" : "#939597"),
                    border: "1px solid #ced4da",
                    flexShrink: 0,
                  }}
                  title="Color Swatch"
                />
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 500, marginBottom: "8px", display: "block", fontSize: "14px" }}>
                Theme Live Preview
              </label>
              <div
                style={{
                  padding: "20px",
                  background: "#f8f9fa",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  border: "1px solid #e9ecef",
                }}
              >
                <button
                  type="button"
                  style={{
                    backgroundColor: editingType === "primary" ? tempColor : primaryColor,
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "5px",
                    padding: "8px 20px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "default",
                  }}
                >
                  Primary Action
                </button>
                <button
                  type="button"
                  style={{
                    backgroundColor: editingType === "secondary" ? tempColor : secondaryColor,
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "5px",
                    padding: "8px 20px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "default",
                  }}
                >
                  Secondary Action
                </button>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowColorModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveThemeColor}
              disabled={savingColor}
            >
              {savingColor ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
        {loading ? <Loader /> : null}
      </div>
    </>
  );
};

export default Settings;
